// ============================================================
// Besprechungsprotokolle — bearbeitbare, speicherbare Protokolle.
// Liste + Editor (Titel/Datum/Firma/Teilnehmer/Notizen/Aufgaben/
// nächste Sitzung) + Druckansicht. Verknüpfbar mit Kalender-Terminen
// (Handoff aus dem Kalender öffnet/erzeugt das Protokoll zum Termin).
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { useMandantStore } from '../../store/mandantStore';
import { useHandoffStore } from '../../store/handoffStore';
import { useFocusStore } from '../../store/focusStore';
import ErinnerungButton from '../../components/ErinnerungButton';
import { prepareAnhang } from '../../services/captureService';
import {
  loadProtokolle, getProtokoll, findByEvent, saveProtokoll, deleteProtokoll,
  neuesProtokoll, neueAufgabe,
} from '../../services/protokollService';
import type { Protokoll, ProtokollAufgabe, ProtokollAnhang } from '../../db/schema';

function fmt(iso: string): string {
  const [y, m, d] = iso.split('-');
  return d && m && y ? `${d}.${m}.${y}` : iso;
}

export function ProtokolleModul(): React.JSX.Element {
  const mandanten = useMandantStore(s => s.mandanten);
  const getAktiverMandant = useMandantStore(s => s.getAktiverMandant);
  const consume = useHandoffStore(s => s.consume);
  const focusId = useFocusStore(s => (s.module === 'protokolle' ? s.id : null));
  const clearFocus = useFocusStore(s => s.clear);
  const mandantName = useCallback(
    (id?: string) => (id !== undefined && id !== '' ? (mandanten.find(m => m.id === id)?.name ?? id) : '—'),
    [mandanten],
  );

  const [liste, setListe] = useState<Protokoll[]>([]);
  const [aktiv, setAktiv] = useState<Protokoll | null>(null);
  const [dirty, setDirty] = useState(false);
  const [gespeichert, setGespeichert] = useState(false);

  const reload = useCallback(async () => { setListe(await loadProtokolle()); }, []);
  useEffect(() => { void reload(); }, [reload]);

  // Deep-Link aus der globalen Suche: bestimmtes Protokoll öffnen.
  useEffect(() => {
    if (focusId === null) return;
    void getProtokoll(focusId).then(p => { if (p !== undefined) { setAktiv(p); setDirty(false); } });
    clearFocus();
  }, [focusId, clearFocus]);

  // Handoff aus dem Kalender: Protokoll zu einem Termin öffnen oder anlegen.
  useEffect(() => {
    const h = consume();
    if (h !== null && h.action === 'protokoll') {
      const ev = h.event;
      void (async () => {
        const vorhanden = await findByEvent(ev.id);
        if (vorhanden !== undefined) { setAktiv(vorhanden); setDirty(false); }
        else {
          const m = getAktiverMandant();
          setAktiv(neuesProtokoll({
            titel: ev.titel, datum: ev.datum, event_id: ev.id,
            ...(ev.uhrzeit !== undefined && ev.uhrzeit !== '' ? { uhrzeit: ev.uhrzeit } : {}),
            ...(ev.uhrzeit_ende !== undefined && ev.uhrzeit_ende !== '' ? { uhrzeit_ende: ev.uhrzeit_ende } : {}),
            ...(ev.ort !== undefined && ev.ort !== '' ? { ort: ev.ort } : {}),
            ...(ev.notizen !== undefined && ev.notizen !== '' ? { notizen: ev.notizen } : {}),
            ...(m !== null ? { mandant_id: m.id } : {}),
          }));
          setDirty(true);
        }
      })();
    }
  }, [consume, getAktiverMandant]);

  function neu(): void {
    const m = getAktiverMandant();
    setAktiv(neuesProtokoll(m !== null ? { mandant_id: m.id } : {}));
    setDirty(true); setGespeichert(false);
  }

  function upd(patch: Partial<Protokoll>): void {
    setAktiv(a => (a ? { ...a, ...patch } : a));
    setDirty(true); setGespeichert(false);
  }
  function clearable(k: 'mandant_id' | 'naechste_sitzung' | 'uhrzeit' | 'uhrzeit_ende' | 'ort', v: string): void {
    setAktiv(a => {
      if (a === null) return a;
      const next: Protokoll = { ...a };
      if (v.trim() === '') delete next[k];
      else next[k] = v;
      return next;
    });
    setDirty(true); setGespeichert(false);
  }
  function setAufgaben(fn: (a: ProtokollAufgabe[]) => ProtokollAufgabe[]): void {
    setAktiv(a => (a ? { ...a, aufgaben: fn(a.aufgaben) } : a));
    setDirty(true); setGespeichert(false);
  }
  function setAnhaenge(fn: (a: ProtokollAnhang[]) => ProtokollAnhang[]): void {
    setAktiv(a => (a ? { ...a, anhaenge: fn(a.anhaenge) } : a));
    setDirty(true); setGespeichert(false);
  }
  async function addAnhang(file: File): Promise<void> {
    try {
      const { b64, mime } = await prepareAnhang(file);
      setAnhaenge(list => [...list, { id: crypto.randomUUID(), name: file.name, b64, mime }]);
    } catch (e) {
      window.alert(`Anhang konnte nicht geladen werden: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  async function speichern(): Promise<void> {
    if (aktiv === null) return;
    await saveProtokoll(aktiv);
    setDirty(false); setGespeichert(true); setTimeout(() => { setGespeichert(false); }, 2000);
    await reload();
    const frisch = await getProtokoll(aktiv.id);
    if (frisch !== undefined) setAktiv(frisch);
  }
  async function loeschen(): Promise<void> {
    if (aktiv === null) return;
    if (!window.confirm('Dieses Protokoll löschen?')) return;
    await deleteProtokoll(aktiv.id); setAktiv(null); await reload();
  }
  function zurueck(): void {
    if (dirty && !window.confirm('Ungespeicherte Änderungen verwerfen?')) return;
    setAktiv(null); void reload();
  }

  // ── LISTE ──────────────────────────────────────────────────────────────
  if (aktiv === null) {
    return (
      <div className="p-6 max-w-4xl mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">📝 Besprechungsprotokolle</h1>
            <p className="text-sm text-gray-500">Bearbeitbar, gespeichert, mit Kalender-Terminen verknüpfbar.</p>
          </div>
          <button onClick={neu} className="btn-primary text-sm">＋ Neues Protokoll</button>
        </div>

        {liste.length === 0 ? (
          <div className="card text-center py-12 text-gray-400">
            <p className="text-4xl mb-2">📝</p>
            <p className="text-sm">Noch keine Protokolle. Lege eins an — oder öffne einen Termin im Kalender und klicke „📝 Protokoll".</p>
          </div>
        ) : (
          <div className="space-y-2">
            {liste.map(p => {
              const offen = p.aufgaben.filter(a => !a.erledigt).length;
              return (
                <button key={p.id} onClick={() => { setAktiv(p); setDirty(false); }}
                  className="card w-full text-left hover:ring-1 hover:ring-blue-300 transition">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 dark:text-gray-100 truncate">{p.titel || 'Besprechung'}</p>
                      <p className="text-xs text-gray-400 truncate">
                        {fmt(p.datum)}{p.uhrzeit ? ` ${p.uhrzeit}${p.uhrzeit_ende ? `–${p.uhrzeit_ende}` : ''}` : ''} · {mandantName(p.mandant_id)}
                        {p.event_id !== undefined && p.event_id !== '' ? ' · 📅 Termin' : ''}
                        {p.anhaenge.length > 0 ? ` · 📎 ${p.anhaenge.length}` : ''}
                      </p>
                    </div>
                    {offen > 0 && (
                      <span className="text-xs bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 px-2 py-0.5 rounded-full flex-shrink-0">
                        {offen} offen
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── EDITOR ─────────────────────────────────────────────────────────────
  const p = aktiv;
  return (
    <div className="p-6 max-w-4xl mx-auto space-y-4">
      <div className="flex items-center justify-between print:hidden">
        <button onClick={zurueck} className="text-sm text-gray-500 hover:text-gray-800 dark:hover:text-gray-200">← Übersicht</button>
        <div className="flex items-center gap-3">
          {gespeichert && <span className="text-xs text-green-600 dark:text-green-400">✓ gespeichert</span>}
          <button onClick={() => window.print()} className="text-sm text-gray-600 dark:text-gray-300 hover:text-blue-600">🖨 Drucken</button>
          <button onClick={() => void loeschen()} className="text-sm text-red-500 hover:text-red-700">Löschen</button>
          <button onClick={() => void speichern()} className="btn-primary text-sm">
            {dirty ? 'Speichern' : '✓ Gespeichert'}
          </button>
        </div>
      </div>

      <div className="card space-y-4 print:hidden">
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block sm:col-span-2">
            <span className="label-text">Titel / Termin</span>
            <input value={p.titel} onChange={e => { upd({ titel: e.target.value }); }} className="input-field" placeholder="z.B. AMARI Termin" />
          </label>
          <label className="block">
            <span className="label-text">Datum</span>
            <input type="date" value={p.datum} onChange={e => { upd({ datum: e.target.value }); }} className="input-field" />
          </label>
          <label className="block">
            <span className="label-text">Beginn</span>
            <input type="time" value={p.uhrzeit ?? ''} onChange={e => { clearable('uhrzeit', e.target.value); }} className="input-field" />
          </label>
          <label className="block">
            <span className="label-text">Ende (optional)</span>
            <input type="time" value={p.uhrzeit_ende ?? ''} onChange={e => { clearable('uhrzeit_ende', e.target.value); }} className="input-field" />
          </label>
          <label className="block">
            <span className="label-text">Firma</span>
            <select value={p.mandant_id ?? ''} onChange={e => { clearable('mandant_id', e.target.value); }} className="input-field">
              <option value="">— keine —</option>
              {mandanten.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </label>
        </div>

        <label className="block">
          <span className="label-text">Ort</span>
          <input value={p.ort ?? ''} onChange={e => { clearable('ort', e.target.value); }} className="input-field"
            placeholder="z.B. Besprechungsraum / Adresse" />
        </label>

        <label className="block">
          <span className="label-text">Teilnehmer</span>
          <textarea value={p.teilnehmer} onChange={e => { upd({ teilnehmer: e.target.value }); }} rows={2} className="input-field"
            placeholder="Name, Funktion – zeilenweise oder mit Komma" />
        </label>

        <label className="block">
          <span className="label-text">Notizen</span>
          <textarea value={p.notizen} onChange={e => { upd({ notizen: e.target.value }); }} rows={7} className="input-field"
            placeholder="Besprechungsnotizen…" />
        </label>

        <div>
          <div className="flex items-center justify-between">
            <span className="label-text">Dokumente</span>
            <label className="text-xs text-blue-600 hover:underline cursor-pointer">
              ＋ Datei/Foto
              <input type="file" accept="image/*,application/pdf" className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) { void addAnhang(f); } e.target.value = ''; }} />
            </label>
          </div>
          <div className="mt-1 space-y-1.5">
            {p.anhaenge.length === 0 && <p className="text-xs text-gray-400">Keine Dokumente. (Fotos werden verkleinert, PDF bis 4 MB.)</p>}
            {p.anhaenge.map(a => (
              <div key={a.id} className="flex items-center gap-2">
                {a.mime.startsWith('image/') ? (
                  <img src={`data:${a.mime};base64,${a.b64}`} alt={a.name}
                    className="h-12 w-12 object-cover rounded border border-gray-200 dark:border-gray-700 flex-shrink-0" />
                ) : (
                  <span className="text-2xl flex-shrink-0" aria-hidden>📄</span>
                )}
                <a href={`data:${a.mime};base64,${a.b64}`}
                  {...(a.mime === 'application/pdf' ? { download: a.name } : { target: '_blank', rel: 'noopener' })}
                  className="text-sm text-blue-600 hover:underline flex-1 min-w-0 truncate">{a.name}</a>
                <button onClick={() => { setAnhaenge(list => list.filter(x => x.id !== a.id)); }}
                  className="text-gray-300 hover:text-red-500 text-lg leading-none flex-shrink-0" title="Entfernen">×</button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <span className="label-text">Aufgaben</span>
            <button onClick={() => { setAufgaben(a => [...a, neueAufgabe()]); }} className="text-xs text-blue-600 hover:underline">＋ Aufgabe</button>
          </div>
          <div className="space-y-1.5 mt-1">
            {p.aufgaben.length === 0 && <p className="text-xs text-gray-400">Keine Aufgaben.</p>}
            {p.aufgaben.map(a => (
              <div key={a.id} className="flex items-center gap-2">
                <input type="checkbox" checked={a.erledigt}
                  onChange={() => { setAufgaben(list => list.map(x => x.id === a.id ? { ...x, erledigt: !x.erledigt } : x)); }}
                  className="rounded border-gray-300 text-green-600 flex-shrink-0" />
                <input value={a.text}
                  onChange={e => { setAufgaben(list => list.map(x => x.id === a.id ? { ...x, text: e.target.value } : x)); }}
                  className={`input-field flex-1 ${a.erledigt ? 'line-through text-gray-400' : ''}`} placeholder="Aufgabe…" />
                <ErinnerungButton titel={a.text || 'Aufgabe'} typ="TODO"
                  mandantId={p.mandant_id} quelle={`protokoll:${p.id}:${a.id}`}
                  className="text-xs text-gray-400 hover:text-amber-600 flex-shrink-0" />
                <button onClick={() => { setAufgaben(list => list.filter(x => x.id !== a.id)); }}
                  className="text-gray-300 hover:text-red-500 text-lg leading-none flex-shrink-0">×</button>
              </div>
            ))}
          </div>
        </div>

        <label className="block">
          <span className="label-text">Nächste Sitzung (optional)</span>
          <input value={p.naechste_sitzung ?? ''} onChange={e => { clearable('naechste_sitzung', e.target.value); }} className="input-field"
            placeholder="z.B. ASA 23.09.2026" />
        </label>

        {p.event_id !== undefined && p.event_id !== '' && (
          <p className="text-xs text-gray-400">📅 Mit einem Kalender-Termin verknüpft.</p>
        )}
      </div>

      {/* Druckansicht (nur beim Drucken sichtbar) */}
      <div className="hidden print:block text-sm text-black">
        <h1 className="text-lg font-bold">Besprechungsprotokoll</h1>
        <p className="mt-1"><strong>{p.titel || 'Besprechung'}</strong> — {fmt(p.datum)}
          {p.uhrzeit ? `, ${p.uhrzeit}${p.uhrzeit_ende ? `–${p.uhrzeit_ende}` : ''} Uhr` : ''}
          {p.mandant_id !== undefined && p.mandant_id !== '' ? ` · ${mandantName(p.mandant_id)}` : ''}
          {p.ort ? ` · ${p.ort}` : ''}</p>
        {p.teilnehmer.trim() !== '' && <p className="mt-3"><strong>Teilnehmer:</strong> {p.teilnehmer}</p>}
        {p.notizen.trim() !== '' && (
          <div className="mt-3"><strong>Notizen:</strong><p className="whitespace-pre-wrap">{p.notizen}</p></div>
        )}
        {p.aufgaben.length > 0 && (
          <div className="mt-3"><strong>Aufgaben:</strong>
            <ul className="mt-1">{p.aufgaben.map(a => <li key={a.id}>{a.erledigt ? '☑' : '☐'} {a.text}</li>)}</ul>
          </div>
        )}
        {p.anhaenge.length > 0 && (
          <div className="mt-3"><strong>Dokumente:</strong>
            <ul className="mt-1">{p.anhaenge.map(a => <li key={a.id}>{a.name}</li>)}</ul>
            {p.anhaenge.filter(a => a.mime.startsWith('image/')).map(a => (
              <img key={a.id} src={`data:${a.mime};base64,${a.b64}`} alt={a.name} className="mt-2 max-h-64" />
            ))}
          </div>
        )}
        {p.naechste_sitzung !== undefined && p.naechste_sitzung !== '' && (
          <p className="mt-3"><strong>Nächste Sitzung:</strong> {p.naechste_sitzung}</p>
        )}
      </div>
    </div>
  );
}
